import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, query, orderBy, doc, updateDoc, where } from 'firebase/firestore';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import { StyledTableCell, StyledTableRow } from '../../utils/styled';
import { getCuadrillasOnce, updateCuadrillaFirestore } from "../../services/cuadrillasService";
import { releasePreQuantityForOrden, syncPreQuantityForOrdenUpdate } from "../../services/preStockService";
import { filterMaterialesVisibles, isNoDescCodigo, isNoDescMaterial } from "../../utils/specialMaterials";
import { utils, writeFile, read } from 'xlsx';
import moment from "moment";
import MuiAlert from '@mui/material/Alert';
import { Typography, TextField, Box, Autocomplete, Table as MuiTable, TableBody as MuiTableBody, TableCell, TableHead as MuiTableHead, TableRow as MuiTableRow, CircularProgress } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';

const firestore = getFirestore();

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function OTsList() {
    const [cuadrillas, setCuadrillas] = useState([]);
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: '', open: false });
    const [deleteOTDialog, setDeleteOTDialog] = useState({ open: false, cuadrillaId: null, ordenRef: null });
    const [filterCuadrilla, setFilterCuadrilla] = useState(null);
    const [filterFechaDesde, setFilterFechaDesde] = useState('');
    const [filterFechaHasta, setFilterFechaHasta] = useState('');
    const [openFiltersModal, setOpenFiltersModal] = useState(false);
    const [materializesModal, setMaterializesModal] = useState({ open: false, orden: null });
    const [openLoadMaterialesModal, setOpenLoadMaterialesModal] = useState(false);
    const [loadingMateriales, setLoadingMateriales] = useState(false);
    const [materiales, setMateriales] = useState([]);
    const [errorsCargaOTs, setErrorsCargaOTs] = useState([]);
    const [errorsCargaMateriales, setErrorsCargaMateriales] = useState([]);
    const [pendingMaterialesRows, setPendingMaterialesRows] = useState([]);

    useEffect(() => {
        // Necesitamos obtener cuadrillas con docId para poder eliminar OTs
        const fetchCuadrillasWithDocId = async () => {
            try {
                const cuadrillasCollection = collection(firestore, 'cuadrillas');
                const queryCuadrillas = query(cuadrillasCollection, orderBy('id', 'asc'));
                const snapshot = await getDocs(queryCuadrillas);

                const cuadrillasData = snapshot.docs.map(doc => ({
                    ...doc.data(),
                    docId: doc.id
                }));

                // Filtrar cuadrillas excluyendo id = 0 y id = 1, y solo activas (retrocompat: sin campo activa = activa)
                const cuadrillasFiltradas = cuadrillasData.filter(cuadrilla =>
                    cuadrilla.id !== 0 && cuadrilla.id !== 1 && cuadrilla.id !== "0" && cuadrilla.id !== "1" && cuadrilla.activa !== false
                );

                setCuadrillas(cuadrillasFiltradas);
                console.log('🔍 Cuadrillas filtradas (excluidas id 0 y 1):', cuadrillasFiltradas.length);
                console.log('📋 Muestra de cuadrilla:', cuadrillasFiltradas[0]);
            } catch (error) {
                console.error("Error al obtener cuadrillas:", error);
                setSnackbarInfo({
                    text: 'Error al cargar cuadrillas',
                    type: 'error',
                    open: true
                });
            }
        };

        fetchCuadrillasWithDocId();

        // Cargar materiales para búsqueda
        const fetchMateriales = async () => {
            try {
                const materialesCollection = collection(firestore, 'materiales');
                const queryMateriales = query(materialesCollection, orderBy('id', 'asc'));
                const snapshot = await getDocs(queryMateriales);
                const materialesData = snapshot.docs.map(doc => doc.data());
                setMateriales(materialesData);
            } catch (error) {
                console.error("Error al obtener materiales:", error);
            }
        };

        fetchMateriales();
    }, []);

    const handleCloseSanckbar = () => {
        setSnackbarInfo({ ...snackbarInfo, open: false });
    };

    // Función para buscar material por código exacto
    const buscarMaterialPorCodigo = (codigo) => {
        const codigoTrim = codigo.toString().trim();
        return materiales.find(material => {
            if (!material.codigos) return false;
            const codigosArray = material.codigos
                .split(',')
                .map(c => c.trim())
                .map(c => c.toLowerCase());
            return codigosArray.includes(codigoTrim.toLowerCase());
        });
    };

    // Función para descargar plantilla de materiales
    const downloadMaterialesTemplate = () => {
        const exampleData = [
            {
                'OPERADOR DESCARGA': 'u995448',
                'NRO OT': '00312038',
                'COD MATERIAL': 'ABC-001',
                'CANTIDAD': 5
            },
            {
                'OPERADOR DESCARGA': 'u995448',
                'NRO OT': '00312038',
                'COD MATERIAL': 'XYZ-002',
                'CANTIDAD': 3
            },
            {
                'OPERADOR DESCARGA': 'u997693',
                'NRO OT': '00306745',
                'COD MATERIAL': 'ABC-001',
                'CANTIDAD': 2
            }
        ];

        const ws = utils.json_to_sheet(exampleData);
        ws['!cols'] = [
            { wch: 18 }, // OPERADOR DESCARGA
            { wch: 12 }, // NRO OT (workOrder)
            { wch: 15 }, // COD MATERIAL
            { wch: 10 }  // CANTIDAD
        ];

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Plantilla Materiales');
        writeFile(wb, 'plantilla_ot_materiales.xlsx');
    };

    // Función que ejecuta el guardado de materiales en Firestore una vez validados
    const ejecutarCargaMateriales = async (rowsToProcess) => {
        setLoadingMateriales(true);
        setErrorsCargaMateriales([]);

        try {
            const materialesPorOrden = new Map();
            let notFoundMateriales = 0;
            let notFoundOTs = 0;
            const filasFallidas = [];

            console.log('🔄 Procesando materiales validados...');

            for (let i = 0; i < rowsToProcess.length; i++) {
                const row = rowsToProcess[i];
                const { fila, usuario, nroOt, codMaterial, cantidad, materialEncontrado, replacementMaterial } = row;

                if (!usuario || !nroOt) {
                    const motivo = !usuario ? 'Operador faltante' : 'NRO OT faltante';
                    filasFallidas.push({ fila, motivo });
                    console.warn(`Fila ${fila} con datos incompletos: ${motivo}`, row);
                    continue;
                }

                const material = replacementMaterial || materialEncontrado;
                if (!material) {
                    filasFallidas.push({ fila, motivo: `Material no encontrado (código: ${codMaterial})` });
                    notFoundMateriales++;
                    continue;
                }

                const codigoFinal = replacementMaterial
                    ? (replacementMaterial.codigos || '').split(',')[0].trim()
                    : codMaterial.toString().trim();

                // Agrupar por usuario + nroOt (workOrder)
                const key = `${usuario.toString().trim()}|${nroOt.toString().trim()}`;
                if (!materialesPorOrden.has(key)) {
                    materialesPorOrden.set(key, { usuario: usuario.toString().trim(), nroOt: nroOt.toString().trim(), materiales: [] });
                }

                // Buscar si el material ya existe en la orden (mismo materialId)
                const materialesEnOrden = materialesPorOrden.get(key).materiales;
                const materialExistente = materialesEnOrden.find(m => m.materialId === material.id);

                const esNoDesc = isNoDescCodigo(codigoFinal) || isNoDescMaterial(material);
                const cantidadMaterial = esNoDesc
                    ? 0
                    : (cantidad ? parseInt(cantidad, 10) : 1);

                if (materialExistente) {
                    materialExistente.cantidad += cantidadMaterial;
                    console.log(`✅ Material duplicado sumado: Usuario ${usuario} - WO ${nroOt} - ${material.name} (${codigoFinal}) - Cantidad total: ${materialExistente.cantidad}`);
                } else {
                    materialesEnOrden.push({
                        materialId: material.id,
                        codigo: codigoFinal,
                        cantidad: cantidadMaterial,
                        name: material.name,
                        codigos: material.codigos,
                        ...(esNoDesc && { noRequiereMateriales: true }),
                    });
                    console.log(`✅ Material añadido: Usuario ${usuario} - WO ${nroOt} - ${material.name} (${codigoFinal})`);
                }
            }

            // Actualizar las OTs en las cuadrillas
            let updatedCount = 0;
            let errors = [];

            console.log('🔄 Actualizando órdenes...');

            for (const [, ordenData] of materialesPorOrden.entries()) {
                const { usuario, nroOt, materiales: materialesData } = ordenData;

                // Detectar si el valor es formato usuario "u997693" o número de cuadrilla "80CTD672"
                const usuarioStr = usuario.toString().trim();
                const usuarioMatch = usuarioStr.match(/^u\d+$/i);
                const usuarioId = usuarioMatch ? usuarioStr.toLowerCase() : usuarioStr;

                try {
                    // Buscar cuadrilla por campo 'usuario' (u<números>) o por campo 'numero' (80CTD...)
                    let cuadrillaSnapshot;
                    if (usuarioMatch) {
                        const cuadrillaQuery = query(
                            collection(firestore, 'cuadrillas'),
                            where('usuario', '==', usuarioId)
                        );
                        cuadrillaSnapshot = await getDocs(cuadrillaQuery);
                    } else {
                        const cuadrillaQuery = query(
                            collection(firestore, 'cuadrillas'),
                            where('numero', '==', usuarioId)
                        );
                        cuadrillaSnapshot = await getDocs(cuadrillaQuery);
                    }

                    if (cuadrillaSnapshot.empty) {
                        filasFallidas.push({
                            operador: usuario,
                            wo: nroOt,
                            motivo: `Operador no encontrado (${usuarioId})`
                        });
                        console.warn(`Cuadrilla/Usuario no encontrado: ${usuarioId} (original: ${usuario})`);
                        notFoundOTs++;
                        continue;
                    }

                    const cuadrillaEncontrada = {
                        ...cuadrillaSnapshot.docs[0].data(),
                        docId: cuadrillaSnapshot.docs[0].id
                    };

                    // Buscar orden por workOrder (que corresponde a nroOt)
                    if (!cuadrillaEncontrada.ordenes) {
                        filasFallidas.push({
                            operador: usuario,
                            wo: nroOt,
                            motivo: `El operador ${usuarioId} no tiene órdenes`
                        });
                        console.warn(`Cuadrilla ${usuario} no tiene órdenes`);
                        notFoundOTs++;
                        continue;
                    }

                    const ordenIndex = cuadrillaEncontrada.ordenes.findIndex(
                        o => o.workOrder && o.workOrder.toString().trim() === nroOt.toString().trim()
                    );

                    if (ordenIndex === -1) {
                        filasFallidas.push({
                            operador: usuario,
                            wo: nroOt,
                            motivo: `Work Order ${nroOt} no encontrada en operador ${usuarioId}`
                        });
                        console.warn(`Orden con workOrder ${nroOt} no encontrada en cuadrilla ${usuario}`);
                        notFoundOTs++;
                        continue;
                    }

                    const ordenEncontrada = cuadrillaEncontrada.ordenes[ordenIndex];

                    // Actualizar la orden con los nuevos materiales
                    const ordenesActualizadas = [...cuadrillaEncontrada.ordenes];
                    ordenesActualizadas[ordenIndex] = {
                        ...ordenEncontrada,
                        materiales: materialesData,
                        noRequiereMateriales: materialesData.some(isNoDescMaterial),
                    };

                    const docRef = doc(firestore, 'cuadrillas', cuadrillaEncontrada.docId);
                    await updateDoc(docRef, {
                        ordenes: ordenesActualizadas
                    });

                    // Actualizar estado local para reflejar cambios inmediatamente en la tabla
                    setCuadrillas(prev => prev.map(c =>
                        c.docId === cuadrillaEncontrada.docId
                            ? { ...c, ordenes: ordenesActualizadas }
                            : c
                    ));

                    await syncPreQuantityForOrdenUpdate(
                        cuadrillaEncontrada.id,
                        ordenEncontrada.materiales,
                        materialesData
                    );

                    updatedCount++;
                    console.log(`✅ OT ${nroOt} actualizada con ${materialesData.length} materiales`);
                } catch (error) {
                    console.error(`Error al actualizar OT ${nroOt}:`, error);
                    errors.push(`Error en OT ${nroOt}`);
                    notFoundOTs++;
                }
            }

            setPendingMaterialesRows([]);
            setErrorsCargaMateriales(filasFallidas);
            if (filasFallidas.length === 0) {
                setOpenLoadMaterialesModal(false);
            }
            setLoadingMateriales(false);

            // Mostrar resumen detallado
            let mensaje = ``;
            let tipoSnackbar = updatedCount > 0 ? 'success' : 'error';

            if (updatedCount > 0) {
                mensaje = `✅ Carga completada! Se actualizaron ${updatedCount} órdenes.`;
            } else {
                mensaje = `⚠️ No se actualizaron órdenes. `;
            }

            if (notFoundMateriales > 0) {
                mensaje += `\n❌ ${notFoundMateriales} materiales no encontrados.`;
            }
            if (notFoundOTs > 0) {
                mensaje += `\n❌ ${notFoundOTs} órdenes no encontradas.`;
            }

            if (filasFallidas.length > 0) {
                tipoSnackbar = updatedCount > 0 ? 'warning' : 'error';
                mensaje += `\n⚠️ ${filasFallidas.length} filas con errores:`;
                filasFallidas.slice(0, 5).forEach(fila => {
                    if (fila.fila) {
                        mensaje += `\nFila ${fila.fila}: ${fila.motivo}`;
                    } else {
                        mensaje += `\nOperador ${fila.operador}, WO ${fila.wo}: ${fila.motivo} - `;
                    }
                });
                if (filasFallidas.length > 5) {
                    mensaje += `\n... y ${filasFallidas.length - 5} más.`;
                }
            }

            console.log('📊 Resumen de carga:', {
                procesadas: rowsToProcess.length,
                actualizadas: updatedCount,
                materialesNoEncontrados: notFoundMateriales,
                ordenesNoEncontradas: notFoundOTs,
                filasFallidas: filasFallidas.length
            });

            setSnackbarInfo({
                text: mensaje,
                type: tipoSnackbar,
                open: true
            });
        } catch (error) {
            console.error('Error al guardar materiales:', error);
            setSnackbarInfo({
                text: 'Error al guardar los materiales: ' + error.message,
                type: 'error',
                open: true
            });
            setLoadingMateriales(false);
        }
    };

    // Función para procesar y validar archivo de materiales antes de cargar
    const handleLoadMateriales = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        event.target.value = '';

        setLoadingMateriales(true);
        setErrorsCargaMateriales([]);
        setPendingMaterialesRows([]);
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = utils.sheet_to_json(worksheet, { defval: '' });

                console.log('📋 Datos de la planilla:', jsonData);
                console.log('💾 Materiales disponibles en BD:', materiales.length);

                if (jsonData.length === 0) {
                    setSnackbarInfo({
                        text: 'El archivo está vacío o no tiene datos válidos',
                        type: 'error',
                        open: true
                    });
                    setLoadingMateriales(false);
                    return;
                }

                // Validar columnas requeridas (exactamente estos nombres)
                const requiredColumns = ['OPERADOR DESCARGA', 'NRO OT', 'COD MATERIAL', 'CANTIDAD'];
                const firstRow = jsonData[0];
                const missingColumns = requiredColumns.filter(col => !(col in firstRow));

                if (missingColumns.length > 0) {
                    setSnackbarInfo({
                        text: `Faltan las siguientes columnas requeridas: ${missingColumns.join(', ')}`,
                        type: 'error',
                        open: true
                    });
                    setLoadingMateriales(false);
                    return;
                }

                // Analizar todas las filas para detectar materiales no identificados o incorrectos
                const parsedRows = [];

                for (let rowIndex = 0; rowIndex < jsonData.length; rowIndex++) {
                    const row = jsonData[rowIndex];
                    const usuario = row['OPERADOR DESCARGA'] != null ? String(row['OPERADOR DESCARGA']).trim() : '';
                    const nroOt = row['NRO OT'] != null ? String(row['NRO OT']).trim() : '';
                    const codMaterial = row['COD MATERIAL'] != null ? String(row['COD MATERIAL']).trim() : '';
                    const cantidadRaw = row['CANTIDAD'];

                    // Ignorar filas completamente en blanco
                    if (!usuario && !nroOt && !codMaterial && (cantidadRaw === '' || cantidadRaw == null)) {
                        continue;
                    }

                    const materialMatch = codMaterial ? buscarMaterialPorCodigo(codMaterial) : null;

                    parsedRows.push({
                        id: `row-${rowIndex}`,
                        fila: rowIndex + 2,
                        usuario,
                        nroOt,
                        codMaterial,
                        cantidad: cantidadRaw !== '' && cantidadRaw != null ? cantidadRaw : 1,
                        materialEncontrado: materialMatch || null,
                        isInvalidMaterial: !materialMatch,
                        replacementMaterial: null
                    });
                }

                if (parsedRows.length === 0) {
                    setSnackbarInfo({
                        text: 'No se encontraron filas con datos para procesar',
                        type: 'error',
                        open: true
                    });
                    setLoadingMateriales(false);
                    return;
                }

                const hasInvalidMaterials = parsedRows.some(r => r.isInvalidMaterial);

                if (hasInvalidMaterials) {
                    // Detener la subida automática y mostrar tabla de validación previa
                    setPendingMaterialesRows(parsedRows);
                    setLoadingMateriales(false);
                    return;
                }

                // Si todos los materiales son válidos, ejecutar la carga directamente
                await ejecutarCargaMateriales(parsedRows);
            } catch (error) {
                console.error('Error al procesar archivo:', error);
                setSnackbarInfo({
                    text: 'Error al procesar el archivo: ' + error.message,
                    type: 'error',
                    open: true
                });
                setLoadingMateriales(false);
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const handleReplaceInvalidMaterial = (rowId, selectedMaterial) => {
        setPendingMaterialesRows(prev =>
            prev.map(row => row.id === rowId ? { ...row, replacementMaterial: selectedMaterial } : row)
        );
    };

    const handleReplaceAllSameCode = (codMaterial, selectedMaterial) => {
        const codeNorm = String(codMaterial || '').trim().toLowerCase();
        setPendingMaterialesRows(prev =>
            prev.map(row => (row.isInvalidMaterial && String(row.codMaterial || '').trim().toLowerCase() === codeNorm)
                ? { ...row, replacementMaterial: selectedMaterial }
                : row
            )
        );
    };

    const handleDeleteInvalidMaterialRow = (rowId) => {
        setPendingMaterialesRows(prev => prev.filter(row => row.id !== rowId));
    };

    const handleCancelPendingMateriales = () => {
        setPendingMaterialesRows([]);
        setErrorsCargaMateriales([]);
    };

    const handleConfirmPendingMateriales = async () => {
        if (pendingMaterialesRows.length === 0) return;
        await ejecutarCargaMateriales(pendingMaterialesRows);
    };

    // Función para descargar plantilla de ejemplo
    const downloadExampleTemplate = () => {
        const exampleData = [
            {
                nroOt: '12345',
                cuadrilla: '80CTD672',
                fecha: new Date(2025, 0, 1), // Fecha real de Excel
                cliente: 'Cliente Ejemplo',
                nroCita: 'CITA001',
                workOrder: 'WO-123',
                contrato: '1234567',
                ciudad: 'ASUNCION',
                departamento: 'CENTRAL',
                comentarios: 'Comentario de ejemplo para la OT',
                'No requiere materiales': 'NO',
                'No descargó materiales en Field Manager': 'SI'
            },
            {
                nroOt: '67890',
                cuadrilla: 'test',
                fecha: new Date(2025, 0, 2), // Fecha real de Excel
                cliente: 'Otro Cliente',
                nroCita: '',
                workOrder: '',
                contrato: '7654321',
                ciudad: 'LAMBARE',
                departamento: 'CENTRAL',
                comentarios: 'Otro comentario de ejemplo',
                'No requiere materiales': 'SI',
                'No descargó materiales en Field Manager': 'NO'
            }
        ];

        const ws = utils.json_to_sheet(exampleData);
        ws['!cols'] = [
            { wch: 10 }, // nroOt
            { wch: 15 }, // cuadrilla
            { wch: 12 }, // fecha
            { wch: 20 }, // cliente
            { wch: 12 }, // nroCita
            { wch: 12 }, // workOrder
            { wch: 12 }, // contrato
            { wch: 15 }, // ciudad
            { wch: 15 }, // departamento
            { wch: 30 }, // comentarios
            { wch: 25 }, // No requiere materiales
            { wch: 35 }  // No descargó materiales en Field Manager
        ];

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Plantilla OTs');
        writeFile(wb, 'plantilla_ordenes.xlsx');
    };

    const downloadErrorsOTs = () => {
        if (errorsCargaOTs.length === 0) return;
        const data = errorsCargaOTs.map(e => ({
            'Fila Excel': e.fila || '',
            'Operador/Cuadrilla': e.operador || '',
            'Nro OT/WO': e.wo || '',
            'Motivo Error': e.motivo || ''
        }));
        const ws = utils.json_to_sheet(data);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Errores Carga OTs');
        writeFile(wb, `errores_carga_ots_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };

    const downloadErrorsMateriales = () => {
        if (errorsCargaMateriales.length === 0) return;
        const data = errorsCargaMateriales.map(e => ({
            'Fila Excel': e.fila || '',
            'Operador/Cuadrilla': e.operador || '',
            'Nro OT/WO': e.wo || '',
            'Motivo Error': e.motivo || ''
        }));
        const ws = utils.json_to_sheet(data);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Errores Carga Materiales');
        writeFile(wb, `errores_carga_materiales_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };

    // Función para procesar la planilla cargada
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploading(true);
        setErrorsCargaOTs([]);
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = utils.sheet_to_json(worksheet);

                console.log('Datos de la planilla:', jsonData);

                if (jsonData.length === 0) {
                    setSnackbarInfo({
                        text: 'El archivo está vacío o no tiene datos válidos',
                        type: 'error',
                        open: true
                    });
                    return;
                }

                // Validar columnas requeridas
                const requiredColumns = ['nroOt', 'cuadrilla', 'fecha'];
                const firstRow = jsonData[0];
                const missingColumns = requiredColumns.filter(col => !(col in firstRow));

                if (missingColumns.length > 0) {
                    setSnackbarInfo({
                        text: `Faltan las siguientes columnas requeridas: ${missingColumns.join(', ')}`,
                        type: 'error',
                        open: true
                    });
                    return;
                }

                // Primero, agrupar órdenes por cuadrilla para procesar en lotes
                const ordenesPorCuadrilla = new Map();
                const filasFallidas = [];
                let notFoundCount = 0;
                let skippedCount = 0;

                console.log('🔄 Agrupando órdenes por cuadrilla...');

                // Paso 1: Agrupar y validar
                for (let rowIndex = 0; rowIndex < jsonData.length; rowIndex++) {
                    const row = jsonData[rowIndex];
                    const { nroOt, cuadrilla: cuadrillaId, fecha, cliente, nroCita, workOrder, contrato, ciudad, departamento, comentarios } = row;
                    const excelRowNumber = rowIndex + 2;

                    if (!nroOt || !cuadrillaId || !fecha) {
                        console.warn('Fila con datos incompletos:', row);
                        skippedCount++;
                        filasFallidas.push({
                            fila: excelRowNumber,
                            operador: cuadrillaId || '',
                            wo: nroOt || '',
                            motivo: 'Datos obligatorios incompletos (nroOt, cuadrilla o fecha vacíos)'
                        });
                        continue;
                    }

                    // Extraer código de cuadrilla (parte antes del guión si existe)
                    let codigoCuadrilla = cuadrillaId.toString().trim();
                    if (codigoCuadrilla.includes(' - ')) {
                        codigoCuadrilla = codigoCuadrilla.split(' - ')[0].trim();
                        console.log(`📝 Código extraído: "${cuadrillaId}" → "${codigoCuadrilla}"`);
                    } else {
                        console.log(`📝 Código sin guión: "${codigoCuadrilla}"`);
                    }

                    // Buscar la cuadrilla por 'usuario' (u<números>) o por 'numero' (80CTD...)
                    let cuadrilla;
                    if (/^u\d+$/i.test(codigoCuadrilla)) {
                        cuadrilla = cuadrillas.find(c => c.usuario && c.usuario.toLowerCase() === codigoCuadrilla.toLowerCase());
                        console.log('🔍 Buscando cuadrilla por usuario:', codigoCuadrilla, 'Encontrada:', !!cuadrilla);
                    } else {
                        cuadrilla = cuadrillas.find(c => c.numero === codigoCuadrilla || c.numero === parseInt(codigoCuadrilla));

                        if (!cuadrilla) {
                            cuadrilla = cuadrillas.find(c =>
                                c.numero && c.numero.toString().startsWith(codigoCuadrilla + ' - ')
                            );
                            if (cuadrilla) {
                                console.log(`🔍 Cuadrilla encontrada por código inicial: "${codigoCuadrilla}" → "${cuadrilla.numero}"`);
                            }
                        }
                        console.log('🔍 Buscando cuadrilla número:', codigoCuadrilla, 'Encontrada:', !!cuadrilla);
                    }

                    if (cuadrilla) {
                        console.log(`✅ Cuadrilla encontrada: ID ${cuadrilla.id}, Número "${cuadrilla.numero}"`);
                    } else {
                        console.log(`❌ No se encontró cuadrilla con: "${codigoCuadrilla}"`);
                        console.log('📋 Códigos disponibles:', cuadrillas.map(c => c.numero).slice(0, 10), '...');

                        const similares = cuadrillas.filter(c =>
                            c.numero && c.numero.toString().includes(codigoCuadrilla)
                        ).slice(0, 5);
                        if (similares.length > 0) {
                            console.log('🔍 Cuadrillas similares encontradas:', similares.map(c => c.numero));
                        }
                    }

                    if (!cuadrilla) {
                        console.warn(`Cuadrilla no encontrada: ${cuadrillaId}`);
                        notFoundCount++;
                        filasFallidas.push({
                            fila: excelRowNumber,
                            operador: cuadrillaId,
                            wo: nroOt,
                            motivo: `Cuadrilla no encontrada: "${cuadrillaId}"`
                        });
                        continue;
                    }

                    // Procesar fecha - convertir a YYYYMMDD
                    let fechaFormateada = '';
                    console.log(`🔍 Procesando fecha: ${fecha} (tipo: ${typeof fecha})`);

                    try {
                        if (fecha instanceof Date) {
                            // Si es un objeto Date de JavaScript (común en Excel)
                            const fechaMoment = moment(fecha);
                            if (fechaMoment.isValid()) {
                                fechaFormateada = fechaMoment.format('YYYYMMDD');
                                console.log(`📅 Fecha Date procesada: ${fecha} → ${fechaFormateada}`);
                            } else {
                                throw new Error('Fecha Date inválida');
                            }
                        } else if (typeof fecha === 'number') {
                            // Si es un número (fecha serial de Excel: días desde 1900-01-01)
                            // Excel cuenta desde 1900-01-01, pero tiene un bug en el día 60 (considera 1900 bisiesto)
                            // Usamos moment para convertir correctamente
                            const fechaBase = moment('1899-12-30'); // Base real de Excel
                            const fechaMoment = fechaBase.add(Math.floor(fecha), 'days');

                            if (fechaMoment.isValid()) {
                                fechaFormateada = fechaMoment.format('YYYYMMDD');
                                console.log(`📅 Fecha Excel serial (${fecha}) convertida: ${fechaFormateada}`);
                            } else {
                                throw new Error('Fecha serial inválida');
                            }
                        } else {
                            // Si es string, intentar varios formatos
                            const fechaString = fecha.toString().trim();

                            // Lista de formatos a intentar (con y sin ceros iniciales)
                            const formatos = [
                                'DD/MM/YYYY',   // 01/01/2026
                                'D/M/YYYY',     // 1/1/2026
                                'D/MM/YYYY',    // 1/01/2026
                                'DD/M/YYYY',    // 01/1/2026
                                'DD-MM-YYYY',   // 01-01-2026
                                'D-M-YYYY',     // 1-1-2026
                                'YYYY-MM-DD',   // 2026-01-01
                                'YYYY/MM/DD'    // 2026/01/01
                            ];

                            let fechaMoment = null;

                            // Intentar cada formato
                            for (const formato of formatos) {
                                fechaMoment = moment(fechaString, formato, true);
                                if (fechaMoment.isValid()) {
                                    console.log(`📅 Fecha parseada con formato ${formato}: "${fechaString}" → ${fechaMoment.format('YYYYMMDD')}`);
                                    break;
                                }
                            }

                            // Si no funciona con formatos estrictos, intentar formato YYYYMMDD directo
                            if (!fechaMoment || !fechaMoment.isValid()) {
                                if (fechaString.length === 8 && !isNaN(fechaString)) {
                                    fechaFormateada = fechaString;
                                    console.log(`📅 Fecha ya en formato YYYYMMDD: ${fechaFormateada}`);
                                }
                            }

                            // Último intento: parseo automático de moment
                            if ((!fechaMoment || !fechaMoment.isValid()) && !fechaFormateada) {
                                fechaMoment = moment(fechaString);
                                if (fechaMoment.isValid()) {
                                    console.log(`📅 Fecha parseada automáticamente: "${fechaString}"`);
                                }
                            }

                            // Convertir a formato final si es válido
                            if (fechaMoment && fechaMoment.isValid() && !fechaFormateada) {
                                fechaFormateada = fechaMoment.format('YYYYMMDD');
                                console.log(`📅 Fecha string procesada: "${fechaString}" → ${fechaFormateada}`);
                            } else if (!fechaFormateada) {
                                throw new Error(`No se pudo parsear la fecha: "${fechaString}"`);
                            }
                        }
                    } catch (error) {
                        console.error(`❌ Error procesando fecha:`, error.message);
                        // Usar valor original como fallback
                        fechaFormateada = fecha.toString();
                        console.warn(`⚠️ Usando valor original: ${fechaFormateada}`);
                    }

                    // Parsear "No requiere materiales"
                    const valNoReq = row['noRequiereMateriales'] || row['No requiere materiales'] || row['No Requiere Materiales'];
                    const normalizedValNoReq = String(valNoReq || '')
                        .trim()
                        .toUpperCase()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "");
                    const noRequiereMateriales = normalizedValNoReq === 'SI';

                    // Parsear "No descargó materiales en Field Manager"
                    const valCheckField = row['checkField'] || row['No descargó materiales en Field Manager'] || row['No descargo materiales en Field Manager'] || row['No descargo materiales'];
                    const normalizedValCheckField = String(valCheckField || '')
                        .trim()
                        .toUpperCase()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "");
                    const checkField = normalizedValCheckField === 'SI';

                    // Preparar la nueva orden
                    const nuevaOrden = {
                        nroOt: nroOt.toString(),
                        fecha: fechaFormateada,
                        cliente: cliente ? cliente.toString() : '',
                        nroCita: nroCita != null && nroCita !== '' ? nroCita.toString() : '',
                        workOrder: workOrder != null && workOrder !== '' ? workOrder.toString() : '',
                        contrato: contrato ? contrato.toString() : '',
                        ciudad: ciudad ? ciudad.toString() : '',
                        departamento: departamento ? departamento.toString() : '',
                        comentarios: comentarios ? comentarios.toString() : '',
                        noRequiereMateriales: noRequiereMateriales,
                        checkField: checkField
                    };

                    // Agrupar por cuadrilla
                    const cuadrillaKey = cuadrilla.docId;
                    if (!ordenesPorCuadrilla.has(cuadrillaKey)) {
                        ordenesPorCuadrilla.set(cuadrillaKey, {
                            cuadrilla: cuadrilla,
                            ordenes: []
                        });
                    }

                    ordenesPorCuadrilla.get(cuadrillaKey).ordenes.push(nuevaOrden);
                }

                console.log(`📊 Resumen agrupación: ${ordenesPorCuadrilla.size} cuadrillas afectadas`);

                // Paso 2: Procesar por lotes por cuadrilla
                let updatedCount = 0;

                for (const [cuadrillaKey, { cuadrilla, ordenes }] of ordenesPorCuadrilla) {
                    try {
                        console.log(`🔄 Procesando ${ordenes.length} órdenes para cuadrilla ${cuadrilla.numero}`);

                        // Obtener órdenes actuales de la cuadrilla
                        const ordenesActuales = cuadrilla.ordenes || [];

                        console.log(`📋 Órdenes actuales en cuadrilla ${cuadrilla.numero}:`, ordenesActuales.map(o => o.nroOt));
                        console.log(`📥 Órdenes a procesar:`, ordenes.map(o => o.nroOt));

                        // Separar órdenes nuevas de las que ya existen
                        const ordenesNuevas = [];
                        let ordenesActualizadas = [...ordenesActuales];
                        let camposActualizados = 0;

                        for (const nuevaOrden of ordenes) {
                            const nroOtNuevo = nuevaOrden.nroOt.toString().trim();
                            const indexExistente = ordenesActualizadas.findIndex(existente => {
                                const nroOtExistente = existente.nroOt ? existente.nroOt.toString().trim() : '';
                                return nroOtExistente === nroOtNuevo;
                            });

                            if (indexExistente === -1) {
                                console.log(`✅ Orden ${nroOtNuevo} es nueva, se agregará`);
                                ordenesNuevas.push(nuevaOrden);
                            } else {
                                // Si ya existe, actualizar solo los campos que estaban vacíos
                                const existente = ordenesActualizadas[indexExistente];
                                const camposAMerge = ['cliente', 'nroCita', 'workOrder', 'contrato', 'ciudad', 'departamento', 'comentarios', 'noRequiereMateriales', 'checkField'];
                                const actualizada = { ...existente };
                                let huboCambio = false;

                                for (const campo of camposAMerge) {
                                    if (campo === 'noRequiereMateriales') {
                                        if (!existente.noRequiereMateriales && nuevaOrden.noRequiereMateriales) {
                                            actualizada.noRequiereMateriales = true;
                                            huboCambio = true;
                                            console.log(`🔄 Orden ${nroOtNuevo}: campo "noRequiereMateriales" actualizado → true`);
                                        }
                                    } else if (campo === 'checkField') {
                                        if (!existente.checkField && nuevaOrden.checkField) {
                                            actualizada.checkField = true;
                                            huboCambio = true;
                                            console.log(`🔄 Orden ${nroOtNuevo}: campo "checkField" actualizado → true`);
                                        }
                                    } else {
                                        const valorExistente = (existente[campo] ?? '').toString().trim();
                                        const valorNuevo = (nuevaOrden[campo] ?? '').toString().trim();
                                        if (!valorExistente && valorNuevo) {
                                            actualizada[campo] = valorNuevo;
                                            huboCambio = true;
                                            console.log(`🔄 Orden ${nroOtNuevo}: campo "${campo}" actualizado → "${valorNuevo}"`);
                                        }
                                    }
                                }

                                if (huboCambio) {
                                    ordenesActualizadas[indexExistente] = actualizada;
                                    camposActualizados++;
                                } else {
                                    console.log(`⚠️ Orden ${nroOtNuevo} ya existe y no hay campos nuevos para actualizar, se omitirá`);
                                }
                            }
                        }

                        const todasLasOrdenes = [...ordenesActualizadas, ...ordenesNuevas];
                        const huboCambios = ordenesNuevas.length > 0 || camposActualizados > 0;

                        if (huboCambios) {
                            console.log(`📝 Actualizando cuadrilla ${cuadrilla.numero}: ${ordenesNuevas.length} nuevas, ${camposActualizados} actualizadas`);

                            // Actualizar en Firestore
                            await updateCuadrillaFirestore(cuadrilla.docId, { ordenes: todasLasOrdenes });

                            // Actualizar estado local
                            setCuadrillas(prev => prev.map(c =>
                                c.docId === cuadrilla.docId
                                    ? { ...c, ordenes: todasLasOrdenes }
                                    : c
                            ));

                            updatedCount += ordenesNuevas.length + camposActualizados;

                            console.log(`✅ Cuadrilla ${cuadrilla.numero} actualizada`);
                        } else {
                            console.log(`ℹ️ Sin cambios para cuadrilla ${cuadrilla.numero}`);
                        }

                    } catch (error) {
                        console.error(`❌ Error actualizando cuadrilla ${cuadrilla.numero}:`, error);
                        filasFallidas.push({
                            operador: cuadrilla.numero,
                            motivo: `Error al guardar en Firestore: ${error.message}`
                        });
                    }
                }

                // Construir mensaje informativo
                let mensaje = `Planilla procesada: ${updatedCount} órdenes agregadas.`;

                if (skippedCount > 0) {
                    mensaje += ` ${skippedCount} filas omitidas por datos incompletos (falta nroOt, cuadrilla o fecha).`;
                }

                if (notFoundCount > 0) {
                    mensaje += ` ${notFoundCount} cuadrillas no encontradas.`;
                }

                setSnackbarInfo({
                    text: mensaje,
                    type: updatedCount > 0 ? 'success' : 'warning',
                    open: true
                });

                setErrorsCargaOTs(filasFallidas);
                if (filasFallidas.length === 0) {
                    setOpenUploadModal(false);
                }

            } catch (error) {
                console.error('Error procesando archivo:', error);
                setSnackbarInfo({
                    text: `Error procesando archivo: ${error.message}`,
                    type: 'error',
                    open: true
                });
            } finally {
                setUploading(false);
                // Limpiar input
                event.target.value = '';
            }
        };

        reader.readAsArrayBuffer(file);
    };

    // Función para eliminar una OT específica
    const handleDeleteOT = async () => {
        const { cuadrillaId, ordenRef } = deleteOTDialog;

        try {
            console.log('🗑️ Eliminando OT:', { cuadrillaId, ordenRef });

            const cuadrilla = cuadrillas.find(c => c.docId === cuadrillaId);
            if (!cuadrilla) {
                console.error('❌ Cuadrilla no encontrada:', cuadrillaId);
                return;
            }

            console.log('📋 Cuadrilla encontrada:', cuadrilla.numero, 'OTs actuales:', cuadrilla.ordenes?.length);

            // Crear array sin la orden a eliminar (buscar por nroOt + fecha para evitar problemas con filtros)
            const ordenesActuales = cuadrilla.ordenes || [];
            const nuevasOrdenes = ordenesActuales.filter(o => {
                const sameNroOt = (o?.nroOt ?? '').toString() === (ordenRef?.nroOt ?? '').toString();
                const sameFecha = (o?.fecha ?? '').toString() === (ordenRef?.fecha ?? '').toString();
                return !(sameNroOt && sameFecha);
            });

            console.log('🔄 Actualizando órdenes:', ordenesActuales.length, '→', nuevasOrdenes.length);

            if (ordenRef?.materiales?.length) {
                await releasePreQuantityForOrden(cuadrilla.id, ordenRef.materiales);
            }

            // Actualizar en Firestore usando el docId correcto
            await updateCuadrillaFirestore(cuadrillaId, { ordenes: nuevasOrdenes });
            console.log('✅ Firestore actualizado');

            // Actualizar estado local
            setCuadrillas(prev => prev.map(c =>
                c.docId === cuadrillaId
                    ? { ...c, ordenes: nuevasOrdenes }
                    : c
            ));
            console.log('✅ Estado local actualizado');

            setSnackbarInfo({
                text: 'Orden eliminada exitosamente',
                type: 'success',
                open: true
            });

        } catch (error) {
            console.error('❌ Error eliminando orden:', error);
            setSnackbarInfo({
                text: `Error al eliminar la orden: ${error.message}`,
                type: 'error',
                open: true
            });
        } finally {
            setDeleteOTDialog({ open: false, cuadrillaId: null, ordenRef: null });
        }
    };

    // Función para abrir el diálogo de confirmación de eliminación
    const openDeleteOTDialog = (cuadrillaId, ordenRef) => {
        setDeleteOTDialog({ open: true, cuadrillaId, ordenRef });
    };

    // Formateador de etiqueta de cuadrilla: usuario - codigo - numero
    const getCuadrillaLabel = (c) => {
        if (!c) return '';
        if (typeof c === 'string') return c;
        const parts = [];
        if (c.usuario) parts.push(c.usuario);
        if (c.codigo) parts.push(c.codigo);
        if (c.numero) parts.push(c.numero);
        return parts.join(' - ') || String(c.id || '');
    };

    // Convertir fechas del input (YYYY-MM-DD) al formato almacenado (YYYYMMDD)
    const fechaDesdeFmt = filterFechaDesde ? filterFechaDesde.replaceAll('-', '') : '';
    const fechaHastaFmt = filterFechaHasta ? filterFechaHasta.replaceAll('-', '') : '';

    // Filtrar cuadrillas por cuadrilla seleccionada/buscada, y filtrar órdenes por rango de fechas
    const cuadrillasFiltradas = (cuadrillas || [])
        .filter(c => {
            if (!filterCuadrilla) return true;
            if (typeof filterCuadrilla === 'object') {
                return (c.id !== undefined && c.id === filterCuadrilla.id) ||
                       (c.docId !== undefined && c.docId === filterCuadrilla.docId);
            }
            if (typeof filterCuadrilla === 'string') {
                const query = filterCuadrilla.trim().toLowerCase();
                if (!query) return true;
                const usuario = (c?.usuario ?? '').toString().toLowerCase();
                const codigo = (c?.codigo ?? '').toString().toLowerCase();
                const numero = (c?.numero ?? '').toString().toLowerCase();
                return usuario.includes(query) || codigo.includes(query) || numero.includes(query);
            }
            return true;
        })
        .map(c => {
            if (!fechaDesdeFmt && !fechaHastaFmt) return c;
            const ordenesFiltradas = (c?.ordenes || []).filter(orden => {
                const fechaOt = (orden?.fecha ?? '').toString().trim();
                if (!fechaOt) return false;
                if (fechaDesdeFmt && fechaOt < fechaDesdeFmt) return false;
                if (fechaHastaFmt && fechaOt > fechaHastaFmt) return false;
                return true;
            });
            return { ...c, ordenes: ordenesFiltradas };
        })
        .filter(c => {
            // Si hay filtro de fechas, esconder cuadrillas sin órdenes que coincidan
            if (fechaDesdeFmt || fechaHastaFmt) {
                return (c?.ordenes?.length || 0) > 0;
            }
            return true;
        });

    // Total de OTs (considerando filtros aplicados)
    const totalOrdenes = cuadrillasFiltradas.reduce(
        (acc, c) => acc + (c?.ordenes?.length || 0),
        0
    );

    // Total de OTs sin filtros aplicados (referencia)
    const totalOrdenesGlobal = (cuadrillas || []).reduce(
        (acc, c) => acc + (c?.ordenes?.length || 0),
        0
    );

    const hayFiltrosActivos = !!(
        filterCuadrilla || filterFechaDesde || filterFechaHasta
    );

    const limpiarFiltros = () => {
        setFilterCuadrilla(null);
        setFilterFechaDesde('');
        setFilterFechaHasta('');
    };

    return (
        <Container component="main" maxWidth="xl">
            <Typography variant="h5">Gestión de Órdenes de Trabajo (OTs)</Typography>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <Button
                        variant="outlined"
                        onClick={() => setOpenUploadModal(true)}
                        startIcon={<UploadFileIcon />}
                        color="primary"
                        style={{ marginRight: '10px' }}
                    >
                        Cargar Nros de OT
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setOpenLoadMaterialesModal(true)}
                        startIcon={<UploadFileIcon />}
                        color="success"
                        style={{ marginRight: '10px' }}
                    >
                        Cargar Materiales
                    </Button>
                </div>
            </div>

            <Box sx={{ display: 'flex', gap: 2, marginBottom: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                    variant="outlined"
                    onClick={() => setOpenFiltersModal(true)}
                    startIcon={<FilterListIcon />}
                >
                    Filtros
                </Button>
                {hayFiltrosActivos && (
                    <Button
                        variant="text"
                        onClick={limpiarFiltros}
                        startIcon={<ClearIcon />}
                        color="error"
                        size="small"
                    >
                        Limpiar
                    </Button>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {filterCuadrilla && (
                        <Chip
                            label={`Cuadrilla: ${typeof filterCuadrilla === 'string' ? filterCuadrilla : getCuadrillaLabel(filterCuadrilla)}`}
                            size="small"
                            onDelete={() => setFilterCuadrilla(null)}
                        />
                    )}
                    {filterFechaDesde && (
                        <Chip
                            label={`Desde: ${moment(filterFechaDesde).format('DD/MM/YYYY')}`}
                            size="small"
                            onDelete={() => setFilterFechaDesde('')}
                        />
                    )}
                    {filterFechaHasta && (
                        <Chip
                            label={`Hasta: ${moment(filterFechaHasta).format('DD/MM/YYYY')}`}
                            size="small"
                            onDelete={() => setFilterFechaHasta('')}
                        />
                    )}
                </Box>
                <Box sx={{ marginLeft: 'auto' }}>
                    <Chip
                        label={
                            hayFiltrosActivos
                                ? `Total OTs: ${totalOrdenes} de ${totalOrdenesGlobal}`
                                : `Total OTs: ${totalOrdenesGlobal}`
                        }
                        color="primary"
                        variant="filled"
                        sx={{ fontWeight: 'bold', fontSize: '0.95em' }}
                    />
                </Box>
            </Box>

            <TableContainer>
                <Table aria-label="table de órdenes">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Id</StyledTableCell>
                            <StyledTableCell>Código</StyledTableCell>
                            <StyledTableCell>Número</StyledTableCell>
                            <StyledTableCell>Usuario</StyledTableCell>
                            <StyledTableCell>Órdenes de Trabajo</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cuadrillasFiltradas.map((cuadrilla, index) => (
                            <StyledTableRow key={cuadrilla?.docId ?? index}>
                                <StyledTableCell>{cuadrilla?.id}</StyledTableCell>
                                <StyledTableCell>{cuadrilla?.codigo}</StyledTableCell>
                                <StyledTableCell>{cuadrilla?.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla?.numero}</StyledTableCell>
                                <StyledTableCell>{cuadrilla?.usuario ?? '-'}</StyledTableCell>
                                <StyledTableCell>
                                    {cuadrilla?.ordenes && cuadrilla.ordenes.length > 0 ? (
                                        <div className="serial-container">
                                            <div className="serial-type">
                                                <span className="title-serial">ÓRDENES ({cuadrilla.ordenes.length})</span>
                                                <div>
                                                    {cuadrilla.ordenes.map((orden, orderIndex) => {
                                                        const fechaOrden = moment(orden?.fecha, "YYYYMMDD");
                                                        const diasTranscurridos = fechaOrden.isValid() ? moment().startOf('day').diff(fechaOrden.startOf('day'), 'days') : 0;
                                                        const esAntigua = diasTranscurridos > 30;
                                                        return (
                                                        <div key={orderIndex} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                                            {esAntigua && (
                                                                <Tooltip title={`Esta OT tiene ${diasTranscurridos} días de antigüedad`} arrow>
                                                                    <WarningAmberIcon
                                                                        fontSize="small"
                                                                        style={{ color: '#ed6c02', marginRight: '6px' }}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                            <p className="value-serial" style={{ margin: 0, marginRight: '8px', color: esAntigua ? '#ed6c02' : undefined, fontWeight: esAntigua ? 600 : undefined }}>
                                                                {fechaOrden.isValid() ? fechaOrden.format("DD/MM/YYYY") : orden?.fecha} | OT: {orden.nroOt} {orden.workOrder && <span style={{ fontSize: '0.9em', fontWeight: 'bold' }}>| WO: {orden.workOrder}</span>}
                                                                {orden.cliente && <span style={{ fontSize: '0.8em', color: '#666' }}> - {orden.cliente}</span>}
                                                                {esAntigua && <span style={{ fontSize: '0.75em', marginLeft: 6, color: '#ed6c02', fontStyle: 'italic' }}>({diasTranscurridos} días)</span>}
                                                            </p>
                                                            {orden.noRequiereMateriales && (
                                                                <Chip
                                                                    label="No requiere materiales"
                                                                    size="small"
                                                                    color="default"
                                                                    sx={{ marginRight: '4px', fontSize: '0.7rem', height: 22 }}
                                                                />
                                                            )}
                                                            {filterMaterialesVisibles(orden.materiales).length > 0 && (
                                                                <IconButton
                                                                    size="small"
                                                                    color="info"
                                                                    onClick={() => setMaterializesModal({ open: true, orden })}
                                                                    title={`Ver ${filterMaterialesVisibles(orden.materiales).length} materiales`}
                                                                >
                                                                    <InfoIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => openDeleteOTDialog(cuadrilla.docId, orden)}
                                                                title="Eliminar OT"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="serial-container">
                                            <div className="serial-type">
                                                <span className="title-serial">ÓRDENES (0)</span>
                                                <div>
                                                    <p className="value-serial" style={{ color: '#999', fontStyle: 'italic' }}>Sin órdenes pendientes</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Modal para cargar planilla de órdenes */}
            <Dialog
                open={openUploadModal}
                onClose={() => setOpenUploadModal(false)}
                aria-labelledby="upload-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="upload-dialog-title">
                    Cargar Planilla de Órdenes
                </DialogTitle>
                <DialogContent>
                    <DialogContentText style={{ marginBottom: '20px' }}>
                        Sube una planilla de Excel con las columnas: <strong>nroOt</strong>, <strong>cuadrilla</strong>, <strong>fecha</strong> (obligatorios),
                        y opcionalmente <strong>cliente</strong>, <strong>nroCita</strong>, <strong>workOrder</strong>, <strong>contrato</strong>, <strong>ciudad</strong>, <strong>departamento</strong>, <strong>comentarios</strong>, <strong>No requiere materiales</strong> y <strong>No descargó materiales en Field Manager</strong>.
                    </DialogContentText>
                    <DialogContentText style={{ marginBottom: '20px', color: '#666', fontSize: '0.9em' }}>
                        <strong>Formato de cuadrilla:</strong> Ingrese solo el código alfanumérico de la cuadrilla (ej: "80CTD672").
                        El sistema buscará automáticamente la cuadrilla correspondiente. También acepta códigos completos con nombre y extraerá solo el código.
                    </DialogContentText>
                    <DialogContentText style={{ marginBottom: '20px', color: '#1976d2', fontSize: '0.9em', backgroundColor: '#e3f2fd', padding: '10px', borderRadius: '4px' }}>
                        <strong>💡 Formato de fecha:</strong> Puedes usar fechas reales de Excel (tipo Date) o texto en formato DD/MM/YYYY.
                        El sistema reconoce automáticamente ambos formatos.
                    </DialogContentText>
                    <DialogContentText style={{ marginBottom: '20px', color: '#666', fontSize: '0.9em' }}>
                        <strong>Ejemplo de formato:</strong><br />
                        • nroOt: 12345<br />
                        • cuadrilla: 80CTD672<br />
                        • fecha: 01/01/2025 (puede ser tipo Date de Excel o texto DD/MM/YYYY)<br />
                        • cliente: Nombre del Cliente (opcional)<br />
                        • nroCita: Nro de cita (opcional)<br />
                        • workOrder: Work Order (opcional)<br />
                        • contrato: 1234567 (opcional)<br />
                        • ciudad: ASUNCION (opcional)<br />
                        • departamento: CENTRAL (opcional)<br />
                        • comentarios: Comentario de la OT (opcional)<br />
                        • No requiere materiales: SI / NO (opcional, indica si la tarea técnica no lleva materiales)<br />
                        • No descargó materiales en Field Manager: SI / NO (opcional, registra omisión operativa de descarga para auditoría)
                    </DialogContentText>

                    <input
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        id="upload-file"
                        type="file"
                        onChange={handleFileUpload}
                        disabled={uploading}
                    />
                    <label htmlFor="upload-file">
                        <Button
                            variant="contained"
                            component="span"
                            disabled={uploading}
                            startIcon={<UploadFileIcon />}
                        >
                            {uploading ? 'Procesando...' : 'Seleccionar Archivo'}
                        </Button>
                        <Button
                            variant="text"
                            onClick={downloadExampleTemplate}
                            color="secondary"
                        >
                            Descargar Plantilla
                        </Button>
                    </label>
                    {errorsCargaOTs.length > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#fdf2f2', border: '1px solid #f8b4b4', borderRadius: '4px' }}>
                            <Typography variant="body2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Se encontraron {errorsCargaOTs.length} errores/advertencias en la última carga.
                            </Typography>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={downloadErrorsOTs}
                                startIcon={<FileDownloadIcon />}
                            >
                                Descargar Planilla de Errores
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenUploadModal(false); setErrorsCargaOTs([]); }} disabled={uploading}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de carga de materiales */}
            <Dialog
                open={openLoadMaterialesModal}
                onClose={() => {
                    if (!loadingMateriales) {
                        setOpenLoadMaterialesModal(false);
                        setPendingMaterialesRows([]);
                        setErrorsCargaMateriales([]);
                    }
                }}
                maxWidth={pendingMaterialesRows.length > 0 ? "lg" : "sm"}
                fullWidth
            >
                <DialogTitle>
                    Cargar Materiales a Órdenes de Trabajo
                </DialogTitle>
                <DialogContent>
                    {pendingMaterialesRows.length === 0 && (
                        <>
                            <DialogContentText style={{ marginBottom: '20px' }}>
                                Sube una planilla de Excel con las columnas: <strong>OPERADOR DESCARGA</strong>, <strong>NRO OT</strong> (workOrder), <strong>COD MATERIAL</strong>, <strong>CANTIDAD</strong>
                            </DialogContentText>
                            <DialogContentText style={{ marginBottom: '20px', color: '#666', fontSize: '0.9em' }}>
                                <strong>Búsqueda:</strong> El sistema extrae el ID del operador (formato u+números), lo busca exacto en la cuadrilla, luego busca la orden por work order. Los códigos de material se validan previamente contra los materiales registrados en el sistema.
                            </DialogContentText>
                            <DialogContentText style={{ marginBottom: '20px', color: '#1976d2', fontSize: '0.9em', backgroundColor: '#e3f2fd', padding: '10px', borderRadius: '4px' }}>
                                <strong>💡 Ejemplo:</strong><br />
                                • OPERADOR DESCARGA: u995448<br />
                                • NRO OT: 00312038<br />
                                • COD MATERIAL: ABC-001<br />
                                • CANTIDAD: 5
                            </DialogContentText>
                        </>
                    )}

                    {loadingMateriales ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <CircularProgress />
                        </div>
                    ) : pendingMaterialesRows.length > 0 ? (
                        (() => {
                            const invalidRows = pendingMaterialesRows.filter(r => r.isInvalidMaterial);
                            const unresolvedRows = invalidRows.filter(r => !r.replacementMaterial);
                            const validMaterialOptions = materiales.filter(m => m && m.codigos && m.name);

                            return (
                                <Box>
                                    <MuiAlert
                                        severity={unresolvedRows.length > 0 ? "warning" : "success"}
                                        sx={{ mb: 2 }}
                                    >
                                        {unresolvedRows.length > 0 ? (
                                            <>
                                                <strong>Validación previa: Se detectaron {invalidRows.length} fila(s) con códigos de material incorrectos o no identificados.</strong><br />
                                                No se puede subir la planilla mientras existan materiales sin identificar ({unresolvedRows.length} pendiente{unresolvedRows.length !== 1 ? 's' : ''}). Selecciona un material válido del sistema para sustituirlos o elimina las filas que no correspondan.
                                            </>
                                        ) : (
                                            <>
                                                <strong>¡Todos los materiales fueron corregidos o depurados!</strong><br />
                                                Se procesarán {pendingMaterialesRows.length} registros válidos. Haz clic en <strong>"Confirmar y Cargar Materiales"</strong> para subir la planilla.
                                            </>
                                        )}
                                    </MuiAlert>

                                    {invalidRows.length > 0 && (
                                        <TableContainer sx={{ maxHeight: 420, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                            <MuiTable size="small" stickyHeader>
                                                <MuiTableHead>
                                                    <MuiTableRow>
                                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Código Recibido</TableCell>
                                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Nro OT</TableCell>
                                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Operador</TableCell>
                                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="center">Cantidad</TableCell>
                                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', minWidth: 320 }}>Sustituir por Material Válido (Código - Nombre)</TableCell>
                                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="center">Eliminar</TableCell>
                                                    </MuiTableRow>
                                                </MuiTableHead>
                                                <MuiTableBody>
                                                    {invalidRows.map((row) => {
                                                        const sameCodeOtherRows = invalidRows.filter(
                                                            r => r.id !== row.id &&
                                                                String(r.codMaterial || '').trim().toLowerCase() === String(row.codMaterial || '').trim().toLowerCase() &&
                                                                r.replacementMaterial?.id !== row.replacementMaterial?.id
                                                        );

                                                        return (
                                                            <MuiTableRow
                                                                key={row.id}
                                                                sx={{
                                                                    backgroundColor: row.replacementMaterial ? '#f1f8e9' : '#fff8f8'
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    <Chip
                                                                        label={row.codMaterial || 'Sin código'}
                                                                        size="small"
                                                                        color={row.replacementMaterial ? "default" : "error"}
                                                                        variant="outlined"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            textDecoration: row.replacementMaterial ? 'line-through' : 'none'
                                                                        }}
                                                                    />
                                                                    <Typography variant="caption" display="block" color="textSecondary">
                                                                        Fila {row.fila}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell sx={{ fontWeight: 500 }}>{row.nroOt || '-'}</TableCell>
                                                                <TableCell>{row.usuario || '-'}</TableCell>
                                                                <TableCell align="center">{row.cantidad}</TableCell>
                                                                <TableCell>
                                                                    <Autocomplete
                                                                        size="small"
                                                                        options={validMaterialOptions}
                                                                        value={row.replacementMaterial}
                                                                        onChange={(_, newValue) => handleReplaceInvalidMaterial(row.id, newValue)}
                                                                        getOptionLabel={(option) => `${option.codigos} - ${option.name}`}
                                                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                                        renderOption={(props, option) => (
                                                                            <li {...props} key={option.id}>
                                                                                <Box>
                                                                                    <Typography variant="body2">
                                                                                        <strong>{option.codigos}</strong> — {option.name}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </li>
                                                                        )}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                placeholder="Buscar por código o nombre del material..."
                                                                                error={!row.replacementMaterial}
                                                                            />
                                                                        )}
                                                                    />
                                                                    {row.replacementMaterial && row.codMaterial && sameCodeOtherRows.length > 0 && (
                                                                        <Button
                                                                            size="small"
                                                                            variant="text"
                                                                            sx={{ mt: 0.5, p: 0, fontSize: '0.72rem', textTransform: 'none' }}
                                                                            onClick={() => handleReplaceAllSameCode(row.codMaterial, row.replacementMaterial)}
                                                                        >
                                                                            Aplicar a las demás filas con código "{row.codMaterial}" ({sameCodeOtherRows.length})
                                                                        </Button>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Tooltip title="Eliminar esta fila incorrecta">
                                                                        <IconButton
                                                                            color="error"
                                                                            size="small"
                                                                            onClick={() => handleDeleteInvalidMaterialRow(row.id)}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </MuiTableRow>
                                                        );
                                                    })}
                                                </MuiTableBody>
                                            </MuiTable>
                                        </TableContainer>
                                    )}
                                </Box>
                            );
                        })()
                    ) : (
                        <>
                            <input
                                accept=".xlsx,.xls"
                                style={{ display: 'none' }}
                                id="upload-materiales-file"
                                type="file"
                                onChange={handleLoadMateriales}
                                disabled={loadingMateriales}
                            />
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <label htmlFor="upload-materiales-file" style={{ flex: 1 }}>
                                    <Button
                                        variant="contained"
                                        component="span"
                                        disabled={loadingMateriales}
                                        startIcon={<UploadFileIcon />}
                                        fullWidth
                                    >
                                        {loadingMateriales ? 'Procesando...' : 'Seleccionar Archivo'}
                                    </Button>
                                </label>
                                <Button
                                    variant="text"
                                    onClick={downloadMaterialesTemplate}
                                    color="secondary"
                                >
                                    Descargar Plantilla
                                </Button>
                            </div>
                        </>
                    )}
                    {errorsCargaMateriales.length > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#fdf2f2', border: '1px solid #f8b4b4', borderRadius: '4px' }}>
                            <Typography variant="body2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Se encontraron {errorsCargaMateriales.length} errores/advertencias en la última carga.
                            </Typography>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={downloadErrorsMateriales}
                                startIcon={<FileDownloadIcon />}
                            >
                                Descargar Planilla de Errores
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {pendingMaterialesRows.length > 0 && (
                        <>
                            <Button
                                onClick={handleCancelPendingMateriales}
                                color="inherit"
                                disabled={loadingMateriales}
                            >
                                Elegir otro archivo
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirmPendingMateriales}
                                disabled={
                                    loadingMateriales ||
                                    pendingMaterialesRows.length === 0 ||
                                    pendingMaterialesRows.some(r => r.isInvalidMaterial && !r.replacementMaterial)
                                }
                            >
                                Confirmar y Cargar Materiales ({pendingMaterialesRows.length})
                            </Button>
                        </>
                    )}
                    <Button
                        onClick={() => {
                            setOpenLoadMaterialesModal(false);
                            setPendingMaterialesRows([]);
                            setErrorsCargaMateriales([]);
                        }}
                        disabled={loadingMateriales}
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de confirmación para eliminar OT */}
            <Dialog
                open={deleteOTDialog.open}
                onClose={() => setDeleteOTDialog({ open: false, cuadrillaId: null, ordenRef: null })}
                aria-labelledby="delete-ot-dialog-title"
            >
                <DialogTitle id="delete-ot-dialog-title">
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro de que deseas eliminar esta orden de trabajo? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteOTDialog({ open: false, cuadrillaId: null, ordenRef: null })}
                        color="primary"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleDeleteOT}
                        color="error"
                        variant="contained"
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de filtros agrupados */}
            <Dialog
                open={openFiltersModal}
                onClose={() => setOpenFiltersModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Filtros</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 1 }}>
                        <Autocomplete
                            size="small"
                            freeSolo
                            options={cuadrillas}
                            value={filterCuadrilla}
                            onChange={(_, value) => setFilterCuadrilla(value ?? null)}
                            onInputChange={(_, value, reason) => {
                                if (reason === 'input') {
                                    setFilterCuadrilla(value ?? null);
                                }
                            }}
                            getOptionLabel={getCuadrillaLabel}
                            isOptionEqualToValue={(option, value) => {
                                if (!option || !value) return false;
                                if (typeof value === 'string') return false;
                                return option.id === value.id || option.docId === value.docId;
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Filtrar por Cuadrilla" placeholder="Buscar por usuario, código o número" />
                            )}
                        />
                        <Typography variant="subtitle2" sx={{ marginTop: 1 }}>
                            Rango de fechas
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                size="small"
                                label="Desde"
                                type="date"
                                value={filterFechaDesde}
                                onChange={(e) => setFilterFechaDesde(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                size="small"
                                label="Hasta"
                                type="date"
                                value={filterFechaHasta}
                                onChange={(e) => setFilterFechaHasta(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={limpiarFiltros} color="error" startIcon={<ClearIcon />}>
                        Limpiar filtros
                    </Button>
                    <Button onClick={() => setOpenFiltersModal(false)} variant="contained">
                        Aplicar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarInfo.open}
                // autoHideDuration={4000}
                onClose={handleCloseSanckbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSanckbar} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>

            {/* Modal para ver materiales de OT */}
            <Dialog
                open={materializesModal.open}
                onClose={() => setMaterializesModal({ open: false, orden: null })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Materiales de OT {materializesModal.orden?.nroOt}
                </DialogTitle>
                <DialogContent>
                    {materializesModal.orden?.noRequiereMateriales && (
                        <Chip
                            label="No requiere materiales (Field Manager)"
                            color="default"
                            sx={{ marginTop: 2 }}
                        />
                    )}
                    {filterMaterialesVisibles(materializesModal.orden?.materiales).length > 0 ? (
                        <MuiTable size="small" sx={{ marginTop: 2 }}>
                            <MuiTableHead>
                                <MuiTableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell><strong>Material</strong></TableCell>
                                    <TableCell><strong>Código</strong></TableCell>
                                    <TableCell align="right"><strong>Cantidad</strong></TableCell>
                                </MuiTableRow>
                            </MuiTableHead>
                            <MuiTableBody>
                                {filterMaterialesVisibles(materializesModal.orden.materiales).map((material, index) => (
                                    <MuiTableRow key={index}>
                                        <TableCell>{material.name}</TableCell>
                                        <TableCell>{material.codigo}</TableCell>
                                        <TableCell align="right">{material.cantidad}</TableCell>
                                    </MuiTableRow>
                                ))}
                            </MuiTableBody>
                        </MuiTable>
                    ) : !materializesModal.orden?.noRequiereMateriales && (
                        <Typography variant="body2" color="textSecondary">
                            No hay materiales asociados
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMaterializesModal({ open: false, orden: null })}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default OTsList;
